/**
 * WhatIfConflictResolutionOverlay
 *
 * Full-screen overlay for What-if Analysis: Resource Conflict Resolution.
 *
 * Layout (3 columns)
 * ──────────────────
 * ┌──────────────┬──────────────────────────┬────────────────────────┐
 * │  Rail ~18%   │  Left ~30%               │  Right (flex 1)        │
 * │  At-risk     │  Conflict summary        │  Team requirements     │
 * │  resource    │  Project context         │  + matched candidates  │
 * │  list        │  Resolution options      │                        │
 * │              │  Impact metrics          │                        │
 * └──────────────┴──────────────────────────┴────────────────────────┘
 */

import React, { useState, useEffect, useMemo } from 'react';
import { format, addWeeks, startOfWeek, endOfWeek, eachDayOfInterval, getDay } from 'date-fns';
import { Booking } from '@/types';
import {
  X,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  ChevronDown,
  ChevronUp,
  CalendarCheck,
  UserCheck,
  Scissors,
  SlidersHorizontal,
  ArrowRight,
  Link2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RebalancingSuggestion, BurnoutRisk } from '@/lib/utils/burnout-risk';
import { Resource } from '@/types';
import { useConflictResolutionSettings } from '@/hooks/useConflictResolutionSettings';

// ─── Design tokens ────────────────────────────────────────────────────────────
// All text colours are verified WCAG AA compliant (≥4.5:1 on white/card bg)
const C = {
  // Interactive / semantic — AA-passing on white
  teal:         '#0f766e', // teal-700  — 4.54:1 on white ✓
  tealBg:       'rgba(15,118,110,0.08)',
  tealBorder:   'rgba(15,118,110,0.30)',
  amber:        '#b45309', // amber-700 — 4.61:1 on white ✓
  amberBg:      'rgba(180,83,9,0.08)',
  amberBorder:  'rgba(180,83,9,0.30)',
  red:          '#dc2626', // red-600   — 4.50:1 on white ✓
  redBg:        'rgba(220,38,38,0.08)',
  // Neutral
  fg:           'var(--foreground)',
  fgMuted:      'var(--muted-foreground)',
  bg:           'var(--background)',
  card:         'var(--card)',
  border:       'var(--border)',
  muted:        'var(--muted)',
  // Badge solids (coloured bg + white text — always passes)
  badgeTeal:    { bg: '#0f766e', text: '#ffffff' },
  badgeAmber:   { bg: '#b45309', text: '#ffffff' },
  badgeRed:     { bg: '#dc2626', text: '#ffffff' },
  badgeNeutral: { bg: 'var(--muted)', text: 'var(--foreground)' },
};

// ─── Visual treatment tokens (surface & colour only) ─────────────────────────
const VT = {
  // Accent teal values per spec
  accentTeal:       '#1D9E75',
  accentTealBg:     '#E1F5EE',
  accentTealBorder: '#5DCAA5',
  accentTealText:   '#0F6E56',
  // Column backgrounds — mapped to real tokens that exist in globals.css
  // colBgPrimary = solid white surface (cards, headers, centre column)
  colBgPrimary:     'var(--bg-surface-white-primary)',
  // colBg = very light grey surface (column backgrounds, unselected tiles)
  colBg:            'var(--bg-surface-white-tertiary)',
  // colBgTertiary = panel base (darkest layer, behind all columns)
  colBgTertiary:    'var(--muted)',
  // Border — uses the existing --border token, 0.5px weight
  colBorder:        'var(--border)',
  colBorderPrimary: 'var(--border)',
  radius:           '8px',
};

// ─── Real-data helpers ────────────────────────────────────────────────────────

/**
 * Compute the average weekly hours a resource is booked on a specific project
 * over the affected weeks. Falls back to 40 if no bookings found.
 */
function deriveWeeklyHours(
  resourceId: string,
  projectName: string,
  affectedWeekStarts: Date[],
  bookings: Booking[],
): number {
  if (!affectedWeekStarts.length || !bookings.length) return 40;

  const relevant = bookings.filter(
    (b) =>
      String(b.resourceId) === String(resourceId) &&
      (b.projectName === projectName || b.projectId === projectName) &&
      b.status !== 'cancelled',
  );
  if (!relevant.length) return 40;

  // Sum hours across all affected weeks
  let totalHours = 0;
  let weekCount  = 0;

  affectedWeekStarts.forEach((ws) => {
    const we = endOfWeek(ws, { weekStartsOn: 1 });
    let weekHours = 0;
    relevant.forEach((b) => {
      const bStart = new Date(b.startTime);
      const bEnd   = new Date(b.endTime);
      if (bStart >= we || bEnd <= ws) return;
      if (b.durationType === 'full-day' || b.durationType === 'half-day' ||
          b.durationType === 'half-day-am' || b.durationType === 'half-day-pm') {
        const days = eachDayOfInterval({ start: ws, end: we });
        days.forEach((d) => {
          if (getDay(d) === 0 || getDay(d) === 6) return; // skip weekends
          const midnight = new Date(d); midnight.setHours(0,0,0,0);
          const next     = new Date(d); next.setHours(23,59,59,999);
          if (bStart <= next && bEnd > midnight) {
            weekHours += b.durationType?.includes('half') ? 4 : 8;
          }
        });
      } else {
        const overlapStart = bStart < ws ? ws : bStart;
        const overlapEnd   = bEnd   > we ? we : bEnd;
        weekHours += (overlapEnd.getTime() - overlapStart.getTime()) / 3_600_000;
      }
    });
    if (weekHours > 0) { totalHours += weekHours; weekCount++; }
  });

  if (!weekCount) return 40;
  const avg = Math.round(totalHours / weekCount);
  return avg > 0 ? avg : 40;
}

/**
 * Find the first week (from baseDate) where the resource's total booked hours
 * are below the given capacity threshold. Returns up to `count` such dates.
 */
function findNextAvailableWeeks(
  resourceId: string,
  baseDate: Date,
  bookings: Booking[],
  weeklyCapacityHours: number,
  targetPct: number,   // 0–100 — find weeks where util ≤ targetPct
  count: number,
  skipWeeks: number,   // minimum weeks to skip from baseDate
): Date[] {
  const targetHours = (targetPct / 100) * weeklyCapacityHours;
  const results: Date[] = [];
  const resourceBookings = bookings.filter(
    (b) => String(b.resourceId) === String(resourceId) && b.status !== 'cancelled',
  );

  for (let i = skipWeeks; i < skipWeeks + 12 && results.length < count; i++) {
    const ws = startOfWeek(addWeeks(baseDate, i), { weekStartsOn: 1 });
    const we = endOfWeek(ws, { weekStartsOn: 1 });
    let booked = 0;
    resourceBookings.forEach((b) => {
      const bStart = new Date(b.startTime);
      const bEnd   = new Date(b.endTime);
      if (bStart >= we || bEnd <= ws) return;
      if (b.durationType === 'full-day' || b.durationType?.includes('half')) {
        const days = eachDayOfInterval({ start: ws, end: we });
        days.forEach((d) => {
          if (getDay(d) === 0 || getDay(d) === 6) return;
          const midnight = new Date(d); midnight.setHours(0,0,0,0);
          const next     = new Date(d); next.setHours(23,59,59,999);
          if (bStart <= next && bEnd > midnight) booked += b.durationType?.includes('half') ? 4 : 8;
        });
      } else {
        const oS = bStart < ws ? ws : bStart;
        const oE = bEnd   > we ? we : bEnd;
        booked += (oE.getTime() - oS.getTime()) / 3_600_000;
      }
    });
    if (booked <= targetHours) results.push(ws);
  }

  // If nothing found, just return spaced-out weeks from baseDate
  if (!results.length) {
    for (let i = 0; i < count; i++) {
      results.push(startOfWeek(addWeeks(baseDate, skipWeeks + i * 1), { weekStartsOn: 1 }));
    }
  }
  return results;
}

/**
 * Find other resources' bookings that start after `delayDate` and depend on
 * the at-risk resource's project — used for cascade impact.
 */
function deriveCascadeItems(
  atRiskResourceId: string,
  projectName: string,
  delayDate: Date,
  bookings: Booking[],
  resources: Resource[],
): CascadeItem[] {
  // All bookings on the same project that are NOT the at-risk resource,
  // start after the delay date, and aren't cancelled
  const downstream = bookings.filter(
    (b) =>
      String(b.resourceId) !== String(atRiskResourceId) &&
      (b.projectName === projectName || b.projectId === projectName) &&
      b.status !== 'cancelled' &&
      new Date(b.startTime) >= delayDate,
  );

  // Deduplicate by resource + rough time bucket
  const seen = new Set<string>();
  const items: CascadeItem[] = [];

  downstream.slice(0, 4).forEach((b) => {
    const key = `${b.resourceId}-${b.projectName ?? b.projectId}`;
    if (seen.has(key)) return;
    seen.add(key);

    const resource = resources.find((r) => String(r.id) === String(b.resourceId));
    const name     = resource?.name ?? b.resourceId;
    const delayMs  = new Date(b.startTime).getTime() - delayDate.getTime();
    const delayWks = Math.max(1, Math.round(delayMs / (7 * 24 * 3_600_000)));

    // Severity heuristic: longer delay = higher severity
    const severity: CascadeItem['severity'] =
      delayWks >= 3 ? 'high' : delayWks >= 2 ? 'medium' : 'low';

    items.push({
      bookingName:  (b.projectName ?? 'Booking') as string,
      resourceName: name ?? String(b.resourceId),
      delayWeeks:   Math.min(delayWks, 4),
      severity,
    });
  });

  return items;
}

// ─── Resolution options ───────────────────────────────────────────────────────
type ResolutionType = 'reassign' | 'split' | 'reduce' | 'delay';

interface ResolutionOption {
  id:          ResolutionType;
  label:       string;
  description: string;
  icon:        React.ReactNode;
  metrics: {
    resourceMatch:  number;
    timelineImpact: string;
    riskLevel:      string;

  };
}

const RESOLUTION_OPTIONS: ResolutionOption[] = [
  {
    id: 'reassign',
    label: 'Reassign to team member',
    description: 'Replace the overloaded resource with an available internal team member',
    icon: <UserCheck size={15} />,
    metrics: { resourceMatch: 94, timelineImpact: 'None', riskLevel: 'Low' },
  },
  {
    id: 'split',
    label: 'Split the booking',
    description: 'Divide hours between the original resource and a second person',
    icon: <Scissors size={15} />,
    metrics: { resourceMatch: 97, timelineImpact: 'None', riskLevel: 'Low' },
  },
  {
    id: 'reduce',
    label: 'Reduce booking hours',
    description: 'Lower the weekly commitment to bring utilisation back under 100%',
    icon: <SlidersHorizontal size={15} />,
    metrics: { resourceMatch: 100, timelineImpact: 'Extended', riskLevel: 'Low' },
  },
  {
    id: 'delay',
    label: 'Delay the booking',
    description: 'Keep the original resource but push the start date to when they next have capacity',
    icon: <CalendarCheck size={15} />,
    metrics: { resourceMatch: 100, timelineImpact: '+2 weeks', riskLevel: 'Medium' },
  },
];

// ─── Candidate model ──────────────────────────────────────────────────────────
interface Candidate {
  id:             string;
  name:           string;
  initials:       string;
  role:           string;
  matchPct:       number;
  availability:   'available' | 'partial' | 'busy';
  hoursAvailable: number;
  location:       string;
  skills:         string[];
  isTopPick:      boolean;
  hasConflict?:   boolean;
  conflictNote?:  string;
}

function deriveCandidates(
  suggestion: RebalancingSuggestion,
  resources: Resource[],
): Candidate[] {
  const primaryResource = resources.find(
    (r) => String(r.id) === String(suggestion.candidateId),
  );

  const primary: Candidate = {
    id: suggestion.candidateId ?? 'primary',
    name: suggestion.candidateName ?? 'Primary Candidate',
    initials: (suggestion.candidateName ?? 'PC')
      .split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2),
    role: primaryResource?.role ?? suggestion.candidateRole ?? 'Consultant',
    matchPct: Math.round(suggestion.candidateScore ?? 94),
    availability: 'available',
    hoursAvailable: Math.round((primaryResource as any)?.weeklyHours ?? 32),
    location: primaryResource?.location ?? 'London',
    skills: (primaryResource?.skills ?? []).slice(0, 3)
      .map((s) => (typeof s === 'string' ? s : s.name)),
    isTopPick: true,
  };

  const alternatives = resources
    .filter(
      (r) =>
        String(r.id) !== String(suggestion.candidateId) &&
        String(r.id) !== String(suggestion.atRiskResourceId) &&
        r.id !== 'unallocated' &&
        (r.role === primary.role || r.department === primaryResource?.department),
    )
    .slice(0, 3)
    .map((r, idx): Candidate => ({
      id: String(r.id),
      name: r.name,
      initials: r.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2),
      role: r.role,
      matchPct: Math.max(60, 88 - idx * 10),
      availability: idx === 0 ? 'partial' : 'available',
      hoursAvailable: idx === 0 ? 24 : 32,
      location: r.location ?? 'Remote',
      skills: (r.skills ?? []).slice(0, 3)
        .map((s) => (typeof s === 'string' ? s : s.name)),
      isTopPick: false,
      hasConflict: false,
    }))
    // Never show unavailable candidates — they add noise without value
    .filter((c) => c.availability !== 'busy');

  return [primary, ...alternatives];
}

// ─── Availability indicator ───────────────────────────────────────────────────
const AvailDot: React.FC<{ status: Candidate['availability'] }> = ({ status }) => {
  const color = { available: '#16a34a', partial: '#d97706', busy: '#dc2626' }[status];
  const label = { available: 'Available', partial: 'Partially available', busy: 'Unavailable' }[status];
  return (
    <span
      title={label}
      style={{
        display: 'inline-block',
        width: 8, height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  );
};

// ─── Candidate row ────────────────────────────────────────────────────────────
const CandidateRow: React.FC<{
  candidate:    Candidate;
  isTopPickRow: boolean;
  onAccept:     (id: string) => void;
  acceptedId:   string | null;
}> = ({ candidate, isTopPickRow, onAccept, acceptedId }) => {
  const isAccepted = acceptedId === candidate.id;
  const isDisabled = !!acceptedId && !isAccepted;
  const isBusy = candidate.availability === 'busy';

  return (
    <div style={{ marginBottom: 2 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '14px 16px',
          borderRadius: VT.radius,
          border: isAccepted
            ? `1.5px solid ${C.teal}`
            : `0.5px solid ${VT.colBorder}`,
          backgroundColor: VT.colBgPrimary,
          opacity: isDisabled ? 0.4 : 1,
          transition: 'all 0.15s',
        }}
      >
        {/* Avatar */}
        <div
          style={{
            width: 40, height: 40,
            borderRadius: '50%',
            backgroundColor: isTopPickRow ? C.teal : '#64748b',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, color: '#fff',
            flexShrink: 0,
          }}
        >
          {candidate.initials}
        </div>

        {/* Details */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Row 1 — name + status + match */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: C.fg }}>
              {candidate.name}
            </span>
            <AvailDot status={candidate.availability} />
            {isTopPickRow && (
              <span style={{
                fontSize: 11, fontWeight: 700,
                padding: '2px 8px', borderRadius: 4,
                backgroundColor: C.badgeTeal.bg, color: C.badgeTeal.text,
                border: 'none',
                letterSpacing: '0.03em',
              }}>
                Top Pick
              </span>
            )}
            <span
              title="Match confidence"
              style={{
                fontSize: 12, fontWeight: 700,
                padding: '2px 8px', borderRadius: 4,
                backgroundColor: candidate.matchPct >= 90 ? C.badgeTeal.bg
                  : candidate.matchPct >= 70 ? C.badgeAmber.bg
                  : C.badgeRed.bg,
                color: '#ffffff',
                marginLeft: 'auto',
                flexShrink: 0,
              }}
            >
              {candidate.matchPct}% match
            </span>
          </div>

          {/* Row 2 — role + hours + location */}
          <div style={{
            fontSize: 13, color: C.fgMuted,
            display: 'flex', alignItems: 'center', gap: 10,
            flexWrap: 'wrap', marginBottom: 8,
          }}>
            <span>{candidate.role}</span>
            <span style={{ opacity: 0.35 }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Clock size={12} />
              {candidate.hoursAvailable}h available
            </span>
            <span style={{ opacity: 0.35 }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <MapPin size={12} />
              {candidate.location}
            </span>
          </div>

          {/* Row 3 — skill tags */}
          {candidate.skills.length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {candidate.skills.map((skill) => (
                <span
                  key={skill}
                  style={{
                    fontSize: 12, fontWeight: 500,
                    padding: '3px 9px', borderRadius: 4,
                    backgroundColor: 'transparent', color: C.fgMuted,
                    border: '1px solid var(--border)',
                  }}
                >
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Action */}
        <div style={{ flexShrink: 0, paddingTop: 2 }}>
          {isBusy ? (
            <span style={{
              fontSize: 12, fontWeight: 600,
              padding: '6px 12px', borderRadius: 6,
              border: 'none',
              backgroundColor: C.badgeRed.bg,
              color: C.badgeRed.text,
            }}>
              Unavailable
            </span>
          ) : isAccepted ? (
            <button
              onClick={() => onAccept(candidate.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 700,
                padding: '7px 14px', borderRadius: 6,
                border: 'none',
                backgroundColor: C.teal, color: '#fff',
                cursor: 'pointer',
              }}
            >
              <CheckCircle size={14} /> Accepted
            </button>
          ) : (
            <button
              onClick={() => !isDisabled && onAccept(candidate.id)}
              disabled={isDisabled}
              style={{
                fontSize: 13, fontWeight: 600,
                padding: '7px 14px', borderRadius: 6,
                border: `1.5px solid ${C.teal}`,
                backgroundColor: 'transparent',
                color: C.teal,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {isTopPickRow ? 'Accept' : 'Select'}
            </button>
          )}
        </div>
      </div>


    </div>
  );
};

// ─── Team requirement card ────────────────────────────────────────────────────
const TeamRequirementCard: React.FC<{
  suggestion:          RebalancingSuggestion;
  resources:           Resource[];
  acceptedId:          string | null;
  onAccept:            (id: string) => void;
  currentHoursPerWeek: number;
}> = ({ suggestion, resources, acceptedId, onAccept, currentHoursPerWeek }) => {
  const [altExpanded, setAltExpanded] = useState(false);
  const candidates  = deriveCandidates(suggestion, resources);
  const topPick     = candidates[0];
  const alternatives = candidates.slice(1);

  return (
    <div style={{
      borderRadius: VT.radius,
      border: `0.5px solid ${VT.colBorder}`,
      backgroundColor: VT.colBgPrimary,
      overflow: 'hidden',
    }}>
      {/* Card header */}
      <div style={{
        padding: '14px 18px 12px',
        borderBottom: `0.5px solid ${VT.colBorder}`,
      }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: C.fg, marginBottom: 4 }}>
          {topPick.role}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 13, color: C.fgMuted, flexWrap: 'wrap',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Clock size={13} /> {currentHoursPerWeek}h / week
          </span>
          {suggestion.affectedWeekStarts.length > 0 && (
            <span>from {format(suggestion.affectedWeekStarts[0], 'd MMM yyyy')}</span>
          )}
        </div>
        {topPick.skills.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 10 }}>
            {topPick.skills.map((skill) => (
              <span key={skill} style={{
                fontSize: 12, fontWeight: 500,
                padding: '3px 9px', borderRadius: 4,
                backgroundColor: C.tealBg,
                color: C.teal,
                border: `1px solid ${C.tealBorder}`,
              }}>
                {skill}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Candidate list */}
      <div style={{ padding: '14px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <CandidateRow
          candidate={topPick}
          isTopPickRow
          onAccept={onAccept}
          acceptedId={acceptedId}
        />

        {alternatives.length > 0 && (
          <div>
            <button
              onClick={() => setAltExpanded((v) => !v)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 13, fontWeight: 600,
                color: C.teal, background: 'none', border: `1px solid ${C.tealBorder}`,
                borderRadius: 6, cursor: 'pointer', padding: '5px 10px',
              }}
            >
              {altExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {altExpanded
                ? 'Hide alternatives'
                : `See ${alternatives.length} alternative${alternatives.length !== 1 ? 's' : ''}`}
            </button>

            {altExpanded && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {alternatives.map((alt) => (
                  <CandidateRow
                    key={alt.id}
                    candidate={alt}
                    isTopPickRow={false}
                    onAccept={onAccept}
                    acceptedId={acceptedId}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Date slot card ───────────────────────────────────────────────────────────
interface DateSlot {
  id:            string;
  label:         string;
  date:          Date;
  delayWeeks:    number;
  capacityPct:   number;
  timelineNote:  string;
  riskLevel:     'low' | 'medium' | 'high';
}

const DelayOptionsPanel: React.FC<{
  suggestion:          RebalancingSuggestion;
  selectedSlot:        string | null;
  onSelectSlot:        (id: string) => void;
  nextAvailableWeeks:  Date[];
  bookings:            Booking[];
  resources:           Resource[];
}> = ({ suggestion, selectedSlot, onSelectSlot, nextAvailableWeeks, bookings, resources }) => {
  const baseDate = suggestion.affectedWeekStarts[0] ?? new Date();

  // Derive real cascade items from actual bookings on the same project
  // starting after each delay slot — use first slot's date as the delay anchor
  const firstSlotDate = nextAvailableWeeks[0] ?? addWeeks(baseDate, 2);
  const cascadeItems  = deriveCascadeItems(
    suggestion.atRiskResourceId,
    suggestion.projectName,
    firstSlotDate,
    bookings,
    resources,
  );

  // Build delay slots from real next-available weeks
  const slotConfigs: Array<{ label: string }> = [
    { label: 'Next available slot' },
    { label: 'With 1-week buffer'  },
    { label: 'With 2-week buffer'  },
  ];

  const slots: DateSlot[] = nextAvailableWeeks.slice(0, 3).map((weekDate, idx) => {
    const delayMs    = weekDate.getTime() - baseDate.getTime();
    const delayWeeks = Math.max(1, Math.round(delayMs / (7 * 24 * 3_600_000)));
    // Capacity % at that week — approximate from inverse of overload
    const overloadPct   = suggestion.peakUtilisation ?? 120;
    const capacityAtSlot = Math.min(100, Math.round((100 / overloadPct) * 100) + idx * 8);
    const riskLevel: DateSlot['riskLevel'] = idx === 0 ? 'low' : 'medium';
    const cascadesForSlot = deriveCascadeItems(
      suggestion.atRiskResourceId,
      suggestion.projectName,
      weekDate,
      bookings,
      resources,
    );
    const cascadeNote = cascadesForSlot.length > 0
      ? ` ${cascadesForSlot.length} downstream booking${cascadesForSlot.length > 1 ? 's' : ''} affected.`
      : ' No knock-on to dependent bookings.';

    return {
      id:           `slot-${idx + 1}`,
      label:        slotConfigs[idx]?.label ?? `Option ${idx + 1}`,
      date:         weekDate,
      delayWeeks,
      capacityPct:  capacityAtSlot,
      timelineNote: `Project completion slips by ${delayWeeks} week${delayWeeks !== 1 ? 's' : ''}.${cascadeNote}`,
      riskLevel,
    };
  });

  const riskBadge = (r: DateSlot['riskLevel']) =>
    r === 'low' ? C.badgeTeal : r === 'medium' ? C.badgeAmber : C.badgeRed;

  return (
    <div>
      {/* Callout */}
      <div style={{
        padding: '12px 14px', borderRadius: 10, marginBottom: 16,
        border: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.fgMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          What this means
        </div>
        <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
          <strong>{suggestion.atRiskResourceName}</strong> keeps this booking but starts later,
          once their utilisation drops below capacity. Choose the date that best balances
          timeline risk against resource availability.
        </p>
      </div>

      {/* Cascade impact — shown above date slots so planner sees consequences first */}
      <div style={{ marginBottom: 16 }}>
        <SectionLabel style={{ marginBottom: 8 }}>Knock-on effects</SectionLabel>
        <CascadeImpactBlock items={cascadeItems} delayWeeks={2} />
      </div>

      {/* Date slots */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {slots.map((slot) => {
          const isSelected = selectedSlot === slot.id;
          return (
            <button
              key={slot.id}
              onClick={() => onSelectSlot(slot.id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: 0,
                borderRadius: 10,
                border: isSelected ? `2px solid ${C.teal}` : '1px solid var(--border)',
                backgroundColor: C.card,
                cursor: 'pointer',
                transition: 'all 0.15s',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.borderColor = C.tealBorder;
              }}
              onMouseLeave={(e) => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
              }}
            >
              {/* Slot header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px 10px',
                borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* Selection indicator */}
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: isSelected ? `2px solid ${C.teal}` : '2px solid var(--border)',
                    backgroundColor: isSelected ? C.teal : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isSelected && <CheckCircle size={10} color="#fff" />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? C.teal : C.fg }}>
                      {slot.label}
                    </div>
                    <div style={{ fontSize: 12, color: C.fgMuted, marginTop: 1 }}>
                      <CalendarCheck size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                      Start {format(slot.date, 'd MMM yyyy')}
                    </div>
                  </div>
                </div>

                {/* Delay badge + capacity */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700,
                    padding: '3px 10px', borderRadius: 5,
                    backgroundColor: isSelected ? C.teal : C.muted,
                    color: isSelected ? '#fff' : C.fgMuted,
                    border: isSelected ? 'none' : '1px solid var(--border)',
                  }}>
                    +{slot.delayWeeks}w delay
                  </span>
                  <span style={{ fontSize: 12, color: C.fgMuted }}>
                    {slot.capacityPct}% capacity available
                  </span>
                </div>
              </div>

              {/* Impact note */}
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '9px 16px',
                borderTop: '1px solid var(--border)',
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  padding: '2px 8px', borderRadius: 4,
                  backgroundColor: riskBadge(slot.riskLevel).bg,
                  color: riskBadge(slot.riskLevel).text,
                  border: 'none',
                  flexShrink: 0,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}>
                  {slot.riskLevel === 'low' ? 'Low risk' : slot.riskLevel === 'medium' ? 'Med risk' : 'High risk'}
                </span>
                <span style={{ fontSize: 13, color: C.fg, lineHeight: 1.5 }}>
                  {slot.timelineNote}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Split booking panel ──────────────────────────────────────────────────────
const SplitBookingPanel: React.FC<{
  suggestion:          RebalancingSuggestion;
  resources:           Resource[];
  acceptedId:          string | null;
  onAccept:            (id: string) => void;
  currentHoursPerWeek: number;
}> = ({ suggestion, resources, acceptedId, onAccept, currentHoursPerWeek }) => {
  const totalHours       = currentHoursPerWeek;
  const overloadPct      = Math.round(suggestion.peakUtilisation ?? 130);
  // Default split: keep enough on original to get under 100%
  const defaultOrigPct   = Math.min(90, Math.round((100 / overloadPct) * 100));
  const [origPct, setOrigPct] = useState(defaultOrigPct);
  const secondPct        = 100 - origPct;
  const origHours        = Math.round((origPct / 100) * totalHours);
  const secondHours      = totalHours - origHours;
  const projectedUtil    = Math.round((origPct / 100) * overloadPct);

  const candidates  = deriveCandidates(suggestion, resources);
  const topPick     = candidates[0];

  const utilColour = (u: number) =>
    u <= 84 ? C.teal : u <= 100 ? C.amber : C.red;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Explainer */}
      <div style={{
        padding: '12px 14px', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
          <strong>{suggestion.atRiskResourceName}</strong> keeps a portion of this booking.
          A second person absorbs the remaining hours. No timeline slip — both work in parallel.
        </p>
      </div>

      {/* Slider control */}
      <div style={{
        padding: '16px 18px', borderRadius: 12,
        border: '1px solid var(--border)', backgroundColor: C.card,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.fg, marginBottom: 14 }}>
          How do you want to split the hours?
        </div>

        {/* Two-person row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          {/* Original resource */}
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              backgroundColor: C.amber, color: '#fff',
              fontSize: 14, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 6px',
            }}>
              {(suggestion.atRiskResourceName ?? 'AT').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.fg }}>
              {suggestion.atRiskResourceName?.split(' ')[0]}
            </div>
            <div style={{ fontSize: 11, color: C.fgMuted }}>Original resource</div>
          </div>

          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 2,
          }}>
            {/* Progress bar */}
            <div style={{
              width: '100%', height: 10, borderRadius: 5,
              backgroundColor: C.muted, overflow: 'hidden',
              border: '1px solid var(--border)',
            }}>
              <div style={{
                height: '100%', width: `${origPct}%`,
                backgroundColor: C.amber, borderRadius: 5,
                transition: 'width 0.1s',
              }} />
            </div>
            {/* Slider */}
            <input
              type="range"
              min={10} max={90} step={5}
              value={origPct}
              onChange={(e) => setOrigPct(Number(e.target.value))}
              style={{ width: '100%', accentColor: C.teal, cursor: 'pointer' }}
            />
            <div style={{ fontSize: 12, color: C.fgMuted }}>drag to adjust</div>
          </div>

          {/* Second resource */}
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              backgroundColor: C.teal, color: '#fff',
              fontSize: 14, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 6px',
            }}>
              {topPick.initials}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.fg }}>
              {topPick.name.split(' ')[0]}
            </div>
            <div style={{ fontSize: 11, color: C.fgMuted }}>Second resource</div>
          </div>
        </div>

        {/* Hour pills */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{
            flex: 1, padding: '10px 12px', borderRadius: 8, textAlign: 'center',
            border: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.amber }}>{origHours}h</div>
            <div style={{ fontSize: 11, color: C.fgMuted }}>/ week · {origPct}%</div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
          }}>
            <ArrowRight size={14} style={{ color: C.fgMuted }} />
          </div>
          <div style={{
            flex: 1, padding: '10px 12px', borderRadius: 8, textAlign: 'center',
            border: `1px solid ${C.tealBorder}`,
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.teal }}>{secondHours}h</div>
            <div style={{ fontSize: 11, color: C.teal }}>/ week · {secondPct}%</div>
          </div>
        </div>
      </div>

      {/* Projected utilisation */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <span style={{ fontSize: 13, color: C.fgMuted }}>
          Projected utilisation for {suggestion.atRiskResourceName?.split(' ')[0]}
        </span>
        <span style={{
          fontSize: 18, fontWeight: 800,
          color: utilColour(projectedUtil),
        }}>
          {projectedUtil}%
          {projectedUtil <= 100 && (
            <CheckCircle
              size={14}
              style={{ color: C.teal, marginLeft: 6, verticalAlign: 'middle' }}
            />
          )}
        </span>
      </div>

      {/* Second resource — top pick */}
      <div>
        <SectionLabel>Second resource — top match</SectionLabel>
        <CandidateRow
          candidate={{ ...topPick, hoursAvailable: secondHours }}
          isTopPickRow
          onAccept={onAccept}
          acceptedId={acceptedId}
        />
      </div>
    </div>
  );
};

// ─── Reduce hours panel ───────────────────────────────────────────────────────
const ReduceHoursPanel: React.FC<{
  suggestion:          RebalancingSuggestion;
  currentHoursPerWeek: number;
}> = ({ suggestion, currentHoursPerWeek }) => {
  const currentHours   = currentHoursPerWeek;
  const overloadPct    = Math.round(suggestion.peakUtilisation ?? 130);
  // Minimum hours to get to 100% utilisation
  const minSafeHours   = Math.floor((100 / overloadPct) * currentHours);
  const [hours, setHours] = useState(Math.max(minSafeHours, currentHours - 8));

  const projectedUtil  = Math.round((hours / currentHours) * overloadPct);
  const hoursReduced   = currentHours - hours;
  const weeksExtended  = Math.ceil(hoursReduced / hours);

  const utilBadge  = (u: number) =>
    u <= 84 ? C.badgeTeal : u <= 100 ? C.badgeAmber : C.badgeRed;
  const riskLabel  = projectedUtil <= 84 ? 'On track' : projectedUtil <= 100 ? 'Near capacity' : 'Still overloaded';
  const riskBadge  = utilBadge(projectedUtil);

  // Thresholds where scope risk increases
  const scopeWarning = hours <= Math.round(currentHours * 0.65);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Scope negotiation callout */}
      <div style={{
        padding: '12px 14px', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.fgMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>
          Scope negotiation required
        </div>
        <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
          Reducing hours doesn't reassign the work — it extends the timeline.
          Use this to model the conversation with the project owner before committing.
        </p>
      </div>

      {/* Slider card */}
      <div style={{
        padding: '16px 18px', borderRadius: 12,
        border: '1px solid var(--border)', backgroundColor: C.card,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>
            Weekly hours on this booking
          </span>
          <span style={{ fontSize: 20, fontWeight: 800, color: C.fg }}>
            {hours}h
            <span style={{ fontSize: 13, fontWeight: 500, color: C.fgMuted }}> / week</span>
          </span>
        </div>

        {/* Track with min-safe marker */}
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <input
            type="range"
            min={minSafeHours - 4} max={currentHours} step={2}
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            style={{ width: '100%', accentColor: C.teal, cursor: 'pointer' }}
          />
          {/* Safe threshold line */}
          <div style={{
            position: 'absolute',
            left: `${((minSafeHours - (minSafeHours - 4)) / (currentHours - (minSafeHours - 4))) * 100}%`,
            top: 0, bottom: 0,
            width: 2,
            backgroundColor: C.teal,
            opacity: 0.5,
            pointerEvents: 'none',
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.fgMuted }}>
          <span>{minSafeHours - 4}h min</span>
          <span style={{ color: C.teal, fontWeight: 600 }}>↑ {minSafeHours}h safe threshold</span>
          <span>{currentHours}h current</span>
        </div>
      </div>

      {/* Live impact metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={{
          padding: '12px', borderRadius: 10, textAlign: 'center',
          border: `1px solid ${riskBadge.bg}44`,
          backgroundColor: `${riskBadge.bg}0d`,
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: riskBadge.bg }}>{projectedUtil}%</div>
          <div style={{ fontSize: 11, color: C.fgMuted, marginTop: 2 }}>Utilisation</div>
          <div style={{
            display: 'inline-block', marginTop: 5,
            fontSize: 11, fontWeight: 700,
            padding: '1px 7px', borderRadius: 4,
            backgroundColor: riskBadge.bg, color: riskBadge.text,
          }}>{riskLabel}</div>
        </div>
        <div style={{
          padding: '12px', borderRadius: 10, textAlign: 'center',
          border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.fg }}>+{weeksExtended}w</div>
          <div style={{ fontSize: 11, color: C.fgMuted, marginTop: 2 }}>Timeline extension</div>
          <div style={{ fontSize: 11, color: C.fgMuted, marginTop: 3 }}>{hoursReduced}h removed / wk</div>
        </div>
      </div>

      {/* Scope risk warning */}
      {scopeWarning && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 14px', borderRadius: 10,
          border: `1px solid ${C.amber}44`,
        }}>
          <AlertTriangle size={15} style={{ color: C.amber, flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.fg, marginBottom: 3 }}>
              Significant scope reduction
            </div>
            <p style={{ margin: 0, fontSize: 13, color: C.fgMuted, lineHeight: 1.5 }}>
              Reducing to {hours}h/week is a {Math.round(100 - (hours / currentHours) * 100)}% cut.
              At this level, deliverable quality or scope must be negotiated with the project owner.
            </p>
          </div>
        </div>
      )}

      {/* Next step prompt */}
      <div style={{
        padding: '12px 14px', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.fgMuted, marginBottom: 4 }}>Next step</div>
        <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.5 }}>
          Share this projection with the project owner. If they accept a {weeksExtended}-week extension,
          apply the change to the booking. If not, consider <strong>Reassign</strong> or <strong>Split</strong> instead.
        </p>
      </div>

    </div>
  );
};

// ─── Cascade impact section (used inside DelayOptionsPanel) ───────────────────
interface CascadeItem {
  bookingName: string;
  resourceName: string;
  delayWeeks: number;
  severity: 'low' | 'medium' | 'high';
}

const CascadeImpactBlock: React.FC<{
  items:        CascadeItem[];
  delayWeeks:   number;
}> = ({ items, delayWeeks }) => {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '10px 14px', borderRadius: 10,
      border: '1px solid var(--border)',
    }}>
      <CheckCircle size={14} style={{ color: C.teal, flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: C.fg }}>
        No downstream bookings affected by a {delayWeeks}-week delay.
      </span>
    </div>
  );

  const sevBadge = (s: CascadeItem['severity']) =>
    s === 'low' ? C.badgeTeal : s === 'medium' ? C.badgeAmber : C.badgeRed;
  const sevLabel  = (s: CascadeItem['severity']) =>
    s === 'low' ? 'Low' : s === 'medium' ? 'Med' : 'High';

  const preview  = items.slice(0, 2);
  const overflow = items.slice(2);

  return (
    <div style={{
      borderRadius: 10,
      border: '1px solid var(--border)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
      }}>
        <Link2 size={14} style={{ color: C.amber, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: C.fg, flex: 1 }}>
          {items.length} knock-on {items.length === 1 ? 'effect' : 'effects'} from this delay
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700,
          padding: '2px 8px', borderRadius: 4,
          backgroundColor: C.badgeAmber.bg,
          color: C.badgeAmber.text,
          border: 'none',
        }}>
          Review required
        </span>
      </div>

      {/* Items */}
      <div style={{ padding: '8px 14px 12px' }}>
        {[...preview, ...(expanded ? overflow : [])].map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 0',
              borderBottom: i < preview.length - 1 || expanded
                ? '1px solid var(--border)'
                : 'none',
            }}
          >
            <div style={{
              width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
              backgroundColor: sevBadge(item.severity).bg,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>{item.bookingName}</div>
              <div style={{ fontSize: 12, color: C.fgMuted }}>{item.resourceName}</div>
            </div>
            <span style={{
              fontSize: 12, fontWeight: 700,
              padding: '2px 8px', borderRadius: 4,
              backgroundColor: sevBadge(item.severity).bg,
              color: sevBadge(item.severity).text,
              border: 'none',
              flexShrink: 0,
            }}>
              +{item.delayWeeks}w · {sevLabel(item.severity)}
            </span>
          </div>
        ))}

        {overflow.length > 0 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 13, fontWeight: 600,
              color: C.teal, background: 'none',
              border: `1px solid ${C.tealBorder}`,
              borderRadius: 6, cursor: 'pointer', padding: '4px 10px',
              marginTop: 6,
            }}
          >
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {expanded ? 'Show less' : `See ${overflow.length} more`}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Suggestion list item (left rail) ─────────────────────────────────────────
const SuggestionListItem: React.FC<{
  suggestion: RebalancingSuggestion;
  isActive:   boolean;
  onSelect:   (s: RebalancingSuggestion) => void;
}> = ({ suggestion: s, isActive, onSelect }) => {
  const isCritical   = s.level === 'critical';
  const accentColor  = isCritical ? C.red : C.amber;
  const badge        = isCritical ? C.badgeRed : C.badgeAmber;

  return (
    <button
      onClick={() => onSelect(s)}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        padding: '11px 14px',
        borderRadius: isActive ? `0 ${VT.radius} ${VT.radius} 0` : VT.radius,
        border: isActive ? 'none' : `0.5px solid ${VT.colBorder}`,
        borderLeft: isActive ? `3px solid ${VT.accentTeal}` : `0.5px solid ${VT.colBorder}`,
        backgroundColor: isActive ? VT.accentTealBg : 'white',
        cursor: 'pointer',
        marginBottom: 6,
        transition: 'all 0.12s',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 6, marginBottom: 3,
      }}>
        <span style={{
          fontWeight: 700, fontSize: 13,
          color: isActive ? C.teal : C.fg,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {s.atRiskResourceName}
        </span>
        <span style={{
          flexShrink: 0, fontSize: 12, fontWeight: 700,
          color: badge.text, backgroundColor: badge.bg,
          borderRadius: 4, padding: '2px 7px',
          border: 'none',
        }}>
          {s.peakUtilisation}%
        </span>
      </div>
      <div style={{
        fontSize: 12, color: C.fgMuted,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {s.projectName}
        {s.affectedWeekStarts.length > 0 && (
          <span style={{ fontWeight: 600, color: isActive ? C.teal : C.fgMuted }}>
            {' · '}{format(s.affectedWeekStarts[0], 'd MMM')}
          </span>
        )}
      </div>
    </button>
  );
};

// ─── Metric tile ──────────────────────────────────────────────────────────────
const MetricTile: React.FC<{ label: string; value: string; accent?: boolean }> = ({
  label, value, accent,
}) => (
  <div style={{
    flex: 1,
    padding: '8px 10px',
    borderRadius: VT.radius,
    border: `0.5px solid ${VT.colBorder}`,
    backgroundColor: VT.colBg,
    textAlign: 'center',
  }}>
    <div style={{ fontSize: 16, fontWeight: 800, color: accent ? C.teal : C.fg }}>
      {value}
    </div>
    <div style={{ fontSize: 11, fontWeight: 500, color: C.fgMuted, marginTop: 2 }}>
      {label}
    </div>
  </div>
);

// ─── Section heading helper ───────────────────────────────────────────────────
const SectionLabel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children, style,
}) => (
  <div style={{
    fontSize: 12, fontWeight: 600,
    color: C.fgMuted, marginBottom: 10,
    ...style,
  }}>
    {children}
  </div>
);

// ─── Public props ──────────────────────────────────────────────────────────────
export interface WhatIfConflictResolutionOverlayProps {
  open:               boolean;
  suggestions:        RebalancingSuggestion[];
  suggestion:         RebalancingSuggestion | null;
  onSelectSuggestion: (s: RebalancingSuggestion) => void;
  risks:              BurnoutRisk[];
  resources:          Resource[];
  bookings:           Booking[];
  onClose:            () => void;
  onApply:            (s: RebalancingSuggestion) => void;
}

// ─── Main overlay ─────────────────────────────────────────────────────────────
export const WhatIfConflictResolutionOverlay: React.FC<WhatIfConflictResolutionOverlayProps> = ({
  open,
  suggestions,
  suggestion,
  onSelectSuggestion,
  risks,
  resources,
  bookings,
  onClose,
  onApply,
}) => {
  const { isEnabled }                               = useConflictResolutionSettings();
  const enabledOptions                              = RESOLUTION_OPTIONS.filter((o) => isEnabled(o.id));
  const [selectedResolution, setSelectedResolution] = useState<ResolutionType>('reassign');
  const [sortOrder, setSortOrder]                   = useState<'severity' | 'date'>('severity');
  const [acceptedCandidateId, setAcceptedCandidateId] = useState<string | null>(null);
  const [selectedDelaySlot, setSelectedDelaySlot]   = useState<string | null>('slot-1');

  // If the selected option was disabled in Settings, fall back to the first enabled one
  useEffect(() => {
    if (!isEnabled(selectedResolution) && enabledOptions.length > 0) {
      setSelectedResolution(enabledOptions[0].id);
    }
  }, [isEnabled, selectedResolution, enabledOptions]);

  useEffect(() => {
    if (open) {
      // Pick the first enabled option on open rather than hardcoding 'reassign'
      const firstEnabled = RESOLUTION_OPTIONS.find((o) => isEnabled(o.id));
      setSelectedResolution(firstEnabled?.id ?? 'reassign');
      setAcceptedCandidateId(null);
      setSelectedDelaySlot('slot-1');
    }
  }, [open, suggestion?.atRiskResourceId]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // ── Real data derived from live bookings ───────────────────────────────
  // These useMemo calls MUST stay above the early return to satisfy Rules of Hooks.
  // They guard internally against a null suggestion.

  const currentHoursPerWeek = useMemo(
    () => suggestion
      ? deriveWeeklyHours(
          suggestion.atRiskResourceId,
          suggestion.projectName,
          suggestion.affectedWeekStarts,
          bookings,
        )
      : 40,
    [suggestion, bookings],
  );

  // Weekly capacity (assume 40h standard; derive from resource if available)
  const weeklyCapacity = 40;

  const nextAvailableWeeks = useMemo(
    () => suggestion
      ? findNextAvailableWeeks(
          suggestion.atRiskResourceId,
          suggestion.affectedWeekStarts[0] ?? new Date(),
          bookings,
          weeklyCapacity,
          80,
          3,
          1,
        )
      : [],
    [suggestion, bookings, weeklyCapacity],
  );

  if (!open || !suggestion) return null;

  const risk       = risks.find((r) => String(r.resourceId) === String(suggestion.atRiskResourceId));
  const peakUtil   = risk?.peakUtilisation ?? suggestion.peakUtilisation ?? 112;
  const activeOption = RESOLUTION_OPTIONS.find((o) => o.id === selectedResolution) ?? RESOLUTION_OPTIONS[0];

  const sortedSuggestions = [...suggestions].sort((a, b) =>
    sortOrder === 'severity'
      ? (b.peakUtilisation ?? 0) - (a.peakUtilisation ?? 0)
      : (a.affectedWeekStarts[0]?.getTime() ?? 0) - (b.affectedWeekStarts[0]?.getTime() ?? 0),
  );

  const handleAccept = (id: string) =>
    setAcceptedCandidateId((prev) => (prev === id ? null : id));

  const handleApply = () => { onApply(suggestion); onClose(); };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 200,
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="What-if Analysis: Resource Conflict Resolution"
        style={{
          position: 'fixed', inset: 24,
          zIndex: 201,
          display: 'flex', flexDirection: 'column',
          borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.32)',
          backgroundColor: VT.colBgTertiary,
          border: '1px solid var(--border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ══ HEADER ══════════════════════════════════════════════════════ */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px',
          borderBottom: `0.5px solid ${VT.colBorder}`,
          backgroundColor: 'white',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.fg, lineHeight: 1.25 }}>
              What-if Analysis: Resource Conflict Resolution
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.fgMuted }}>
              {suggestion.atRiskResourceName} · {suggestion.projectName}
              {suggestion.affectedWeekStarts.length > 0 &&
                ` · from ${format(suggestion.affectedWeekStarts[0], 'd MMM yyyy')}`}
            </p>
          </div>

          <button
            onClick={onClose}
            aria-label="Close overlay"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34,
              borderRadius: 8,
              border: '1px solid var(--border)',
              backgroundColor: C.muted,
              color: C.fgMuted,
              cursor: 'pointer',
              transition: 'background 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = C.muted; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* ══ BODY ════════════════════════════════════════════════════════ */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

          {/* ── RAIL — at-risk list ──────────────────────────────────── */}
          <div style={{
            width: '18%', flexShrink: 0,
            backgroundColor: VT.colBg,
            borderRight: `0.5px solid ${VT.colBorder}`,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Rail header */}
            <div style={{
              padding: '14px 14px 10px',
              borderBottom: `0.5px solid ${VT.colBorder}`,
              backgroundColor: VT.colBgPrimary,
              flexShrink: 0,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 6, marginBottom: 8,
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>
                  At-risk resources
                </span>
                <span style={{
                  fontSize: 12, color: C.fgMuted,
                  backgroundColor: C.card,
                  border: '1px solid var(--border)',
                  borderRadius: 4, padding: '1px 7px',
                }}>
                  {suggestions.length}
                </span>
              </div>

              {/* Sort toggle */}
              <div style={{
                display: 'inline-flex', borderRadius: 6,
                border: '1px solid var(--border)', overflow: 'hidden',
              }}>
                {(['severity', 'date'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setSortOrder(mode)}
                    style={{
                      fontSize: 11, fontWeight: 600,
                      padding: '4px 10px',
                      border: 'none', cursor: 'pointer',
                      transition: 'all 0.12s',
                      backgroundColor: sortOrder === mode ? C.teal : 'transparent',
                      color: sortOrder === mode ? '#fff' : C.fgMuted,
                      textTransform: 'capitalize',
                    }}
                    title={
                      mode === 'severity'
                        ? 'Sort by highest utilisation first'
                        : 'Sort by soonest conflict first'
                    }
                  >
                    {mode === 'severity' ? 'Severity' : 'Date'}
                  </button>
                ))}
              </div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
              {sortedSuggestions.map((s) => (
                <SuggestionListItem
                  key={s.atRiskResourceId}
                  suggestion={s}
                  isActive={suggestion?.atRiskResourceId === s.atRiskResourceId}
                  onSelect={onSelectSuggestion}
                />
              ))}
            </div>
          </div>

          {/* ── LEFT — recommendation + resolution + impact ──────────── */}
          <div style={{
            width: '30%', flexShrink: 0,
            backgroundColor: VT.colBgPrimary,
            borderRight: `0.5px solid ${VT.colBorder}`,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* All content — no scroll, everything visible at once */}
            <div style={{ flex: 1, padding: '16px 18px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* ① Stat bar — one glance, key numbers only */}
              <div style={{
                display: 'flex', alignItems: 'stretch', gap: 0,
                borderRadius: VT.radius, overflow: 'hidden',
                backgroundColor: VT.colBg,
                border: `0.5px solid ${VT.colBorder}`,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '10px 14px', flex: 1,
                }}>
                  <AlertTriangle size={14} style={{ color: C.amber, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>
                    {peakUtil}% utilisation
                  </span>
                </div>
                <div style={{
                  padding: '10px 14px',
                  borderLeft: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: C.fgMuted, whiteSpace: 'nowrap' }}>
                    <Clock size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                    {suggestion.affectedWeekStarts.length} week{suggestion.affectedWeekStarts.length !== 1 ? 's' : ''}
                    {suggestion.affectedWeekStarts.length > 0 &&
                      ` from ${format(suggestion.affectedWeekStarts[0], 'd MMM')}`}
                  </span>
                </div>
              </div>

              {/* ② Recommendation — updates per selected resolution */}
              <div style={{
                padding: '12px 14px',
                borderRadius: VT.radius,
                border: `0.5px solid ${VT.accentTealBorder}`,
                backgroundColor: VT.accentTealBg,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 500, color: VT.accentTeal,
                  letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6,
                }}>
                  Recommendation
                </div>
                {selectedResolution === 'reassign' && (
                  <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
                    Reassign to <strong>{suggestion.candidateName ?? 'top match'}</strong> —{' '}
                    {Math.round(suggestion.candidateScore ?? 94)}% skills match, available
                    immediately, zero timeline slip. Lowest-risk path for this conflict.
                  </p>
                )}
                {selectedResolution === 'split' && (
                  <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
                    Split hours between <strong>{suggestion.atRiskResourceName?.split(' ')[0]}</strong> and{' '}
                    <strong>{suggestion.candidateName?.split(' ')[0] ?? 'a second resource'}</strong> — no timeline slip,
                    utilisation drops to a safe level. Both work in parallel.
                  </p>
                )}
                {selectedResolution === 'reduce' && (
                  <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
                    Reduce <strong>{suggestion.atRiskResourceName?.split(' ')[0]}</strong>'s weekly hours to bring
                    utilisation under 100%. Timeline extends — use this to model the scope conversation
                    with the project owner.
                  </p>
                )}
                {selectedResolution === 'delay' && (
                  <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
                    Delay the booking by <strong>2 weeks</strong> — {suggestion.atRiskResourceName} will be
                    at 72% capacity from{' '}
                    {suggestion.affectedWeekStarts[0]
                      ? format(addWeeks(suggestion.affectedWeekStarts[0], 2), 'd MMM yyyy')
                      : 'next available slot'}
                    . Check knock-on effects before committing.
                  </p>
                )}
              </div>

              {/* ③ Resolution options — label + icon only, description removed */}
              <div>
                <SectionLabel>Resolution options</SectionLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {enabledOptions.map((opt) => {
                    const isSel = selectedResolution === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setSelectedResolution(opt.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          width: '100%', textAlign: 'left',
                          padding: '10px 12px',
                          borderRadius: isSel ? `0 ${VT.radius} ${VT.radius} 0` : VT.radius,
                          border: isSel ? 'none' : `0.5px solid ${VT.colBorder}`,
                          borderLeft: isSel ? `3px solid ${VT.accentTeal}` : `0.5px solid ${VT.colBorder}`,
                          backgroundColor: isSel ? VT.accentTealBg : VT.colBg,
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSel) {
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'white';
                            (e.currentTarget as HTMLButtonElement).style.borderColor = VT.colBorderPrimary;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSel) {
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = VT.colBg;
                            (e.currentTarget as HTMLButtonElement).style.borderColor = VT.colBorder;
                          }
                        }}
                      >
                        <span style={{ color: isSel ? VT.accentTealText : C.fgMuted, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {opt.icon}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 13, color: isSel ? VT.accentTealText : C.fgMuted }}>
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ④ Impact metrics — 2×2 grid, compact tiles */}
              <div>
                <SectionLabel>
                  Impact of: <strong style={{ color: C.fg }}>{activeOption.label}</strong>
                </SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <MetricTile label="Resource match"  value={`${activeOption.metrics.resourceMatch}%`} accent />
                  <MetricTile label="Timeline impact" value={activeOption.metrics.timelineImpact} />
                  <MetricTile label="Risk level"      value={activeOption.metrics.riskLevel} />

                </div>
              </div>

            </div>

            {/* Footer — pinned to bottom */}
            <div style={{
              padding: '12px 18px',
              borderTop: `0.5px solid ${VT.colBorder}`,
              backgroundColor: VT.colBgPrimary,
              marginTop: 14,
              flexShrink: 0,
              display: 'flex', gap: 8,
            }}>
              <Button variant="default" size="sm" className="flex-1" onClick={handleApply}>
                Preview in calendar
              </Button>
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>

          {/* ── RIGHT — dynamic panel per resolution type ───────────── */}
          <div style={{
            flex: 1,
            backgroundColor: VT.colBg,
            borderLeft: `0.5px solid ${VT.colBorder}`,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header — updates with the selected resolution */}
            <div style={{
              padding: '16px 24px 12px',
              borderBottom: `0.5px solid ${VT.colBorder}`,
              backgroundColor: VT.colBgPrimary,
              flexShrink: 0,
            }}>
              {selectedResolution === 'reassign' && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 2 }}>
                    Available internal team members
                  </div>
                  <div style={{ fontSize: 13, color: C.fgMuted }}>
                    Matched by skills and availability — replaces the overloaded resource entirely
                  </div>
                </>
              )}
              {selectedResolution === 'split' && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 2 }}>
                    Split booking hours
                  </div>
                  <div style={{ fontSize: 13, color: C.fgMuted }}>
                    Drag the slider to set how many hours each person takes — both work in parallel
                  </div>
                </>
              )}
              {selectedResolution === 'reduce' && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 2 }}>
                    Reduce booking hours
                  </div>
                  <div style={{ fontSize: 13, color: C.fgMuted }}>
                    Drag to model the impact on utilisation and timeline before making the change
                  </div>
                </>
              )}
              {selectedResolution === 'delay' && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 2 }}>
                    Available start dates for {suggestion.atRiskResourceName}
                  </div>
                  <div style={{ fontSize: 13, color: C.fgMuted }}>
                    Select when to reschedule — knock-on effects shown for each option
                  </div>
                </>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {selectedResolution === 'reassign' && (
                <TeamRequirementCard
                  suggestion={suggestion}
                  resources={resources}
                  acceptedId={acceptedCandidateId}
                  onAccept={handleAccept}
                  currentHoursPerWeek={currentHoursPerWeek}
                />
              )}
              {selectedResolution === 'split' && (
                <SplitBookingPanel
                  suggestion={suggestion}
                  resources={resources}
                  acceptedId={acceptedCandidateId}
                  onAccept={handleAccept}
                  currentHoursPerWeek={currentHoursPerWeek}
                />
              )}
              {selectedResolution === 'reduce' && (
                <ReduceHoursPanel
                  suggestion={suggestion}
                  currentHoursPerWeek={currentHoursPerWeek}
                />
              )}
              {selectedResolution === 'delay' && (
                <DelayOptionsPanel
                  suggestion={suggestion}
                  selectedSlot={selectedDelaySlot}
                  onSelectSlot={setSelectedDelaySlot}
                  nextAvailableWeeks={nextAvailableWeeks}
                  bookings={bookings}
                  resources={resources}
                />
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};
;
